import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ITableHeader } from '../../models/ITableHeader';
import { ScrollService } from '../../services/services';
import { DynamicFormComponent, filterData, IOption, QuestionBase } from '../../../public-api';

@Component({
  selector: 'ff-table-container',
  templateUrl: './table-container.component.html',
  styleUrls: ['./table-container.component.scss']
})
export class TableContainerComponent implements OnInit {

  /**
   * Reference to the dynamic form component for the filters
   */
  @ViewChild(DynamicFormComponent) formComponent: DynamicFormComponent;

  /**
   * Title of the table to be displayed
   */
  @Input () title: string = 'Details';

  /**
   * Table headers
   */
  @Input () tableHeaders: ITableHeader[] = [];

  /**
   * Table data to be displayed at the current page
   */
  @Input () page: any[] = [];

  /**
   * Table data for all pages
   */
  @Input () items: any[] = [];

  /**
   * Table filters list
   */
  @Input () filters: any[] = [];

  /**
   * The default column to sort by when the table is loaded
   */
  @Input () defaultSort: string = 'id';

  /**
   * The questions for the filter form
   */
  @Input () questions?: { [key: string]: QuestionBase<any> };

  /**
   * The information to be displayed at the left of the status bar
   * @example { description: 'Total', value: '100' }
   */
  @Input () statusBarLeft?: { description: string, value: string };

  /**
   * The information to be displayed at the right of the status bar
   * @example { description: 'Available', value: '70' }
   */
  @Input () statusBarRight?: { description: string, value: string }[];

  /**
   * Number of assets per page
   */
  @Input () pageSize: number = 10;

  /**
   * Event emitted when the user does a search
   */
  @Output () searchEvent = new EventEmitter<string>();

  @Output () clearFilterEvent = new EventEmitter();

  /**
   * Event emitted when the page is changed
   */
  @Output () pageChange = new EventEmitter<{
    pageNo: number,
    pageSize: number
  }>();

  /**
   * Event emitted when the user sorts the table
   */
  @Output () sortEvent = new EventEmitter<{
    sortTableKey: string,
    sortReverse: boolean,
    columnIndex: number,
    search: string
  }>();

  /**
   * Event emitted when the user filters the table
   */
  @Output () filterEvent = new EventEmitter<any[]>();

  search: string = "";

  _options: IOption[] = [
    { value: "05", text: "5" },
    { value: "10", text: "10" },
    { value: "20", text: "20" },
    { value: "50", text: "50" },
    { value: "100", text: "100" },
  ];

  sortTableKey: string = this.defaultSort;

  sortReverse: boolean = false;

  filteredItemsPreview: any[] = [];

  firstFilterPassed: boolean = false;

  lastFilter: any = [];

  /// Current page
  pageNo: number = 0;

  totalItems: number = 0;

  min = Math.min;

  constructor(
    private _scrollService: ScrollService
  ) { }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    this.filterEvent.emit(this.formComponent?.form?.value);
  }

  changeSort(index: number) {
    this.changeSortingIcon(index);
    this.sortTableKey = this.tableHeaders[index].key;
  }

  changeSortingIcon(index: number) {
    if (this.tableHeaders[index].icon === "south") {
      this.tableHeaders[index].icon = "north";
      this.tableHeaders[index].isActive = true;
      this.sortReverse = true;

      this.tableHeaders.forEach((_, headerIndex) => {
        if (headerIndex !== index) {
          this.tableHeaders[headerIndex].icon = "south";
          this.tableHeaders[headerIndex].isActive = false;
        }
      });
    } else {
      this.tableHeaders[index].icon = "south";
      this.sortReverse = false;
    }
  }

  sortingManager(columnIndex: number) {
    this.changeSort(columnIndex);
    this.pageNo = 0;
    this.sortEvent.emit({
      sortTableKey: this.sortTableKey,
      sortReverse: this.sortReverse,
      columnIndex: columnIndex,
      search: this.search
    });
  }

  nextPage() {
    if ((this.pageNo + 1) * this.pageSize < this.totalItems) {
      this.pageNo++;
      this.pageChange.emit({
        pageNo: this.pageNo,
        pageSize: this.pageSize
      });
    }
  }

  prevPage() {
    if (this.pageNo > 0) {
      this.pageNo--;
      this.pageChange.emit({
        pageNo: this.pageNo,
        pageSize: this.pageSize
      });
    }
  }

  firstPage() {
    this.pageNo = 0;
    this.pageChange.emit({
      pageNo: this.pageNo,
      pageSize: this.pageSize
    });
  }

  lastPage() {
    this.pageNo = Math.floor(((this.totalItems - 1) / this.pageSize));
    this.pageChange.emit({
      pageNo: this.pageNo,
      pageSize: this.pageSize
    });
  }

  onSizeChange() {
    this.firstPage();
  }

  onSearch(search: string) {
    this.search = search;
    this.pageNo = 0;
    this.searchEvent.emit(this.search);
  }

  onScroll() {
    this._scrollService.scrollDetected();
  }

  getOrderFieldArguments() {
    // sortTableKey keys are separated by a period, so we need to split them
    const sortTableKeySplit = this.sortTableKey.split(".");
    // build the order field arguments based on the sortTableKeySplit
    const orderFieldArguments = sortTableKeySplit.reduceRight(
      (acc: any, curr: any) => ({ [curr]: acc }),
      this.sortReverse ? "ASC" : "DESC"
    );
    return orderFieldArguments;
  }

  applyFilters(items: any, formValue: any) {
    this.pageNo = 0;
    this.filteredItemsPreview = filterData(
      items,
      this.filters,
      formValue,
      "AND"
    );
  }

  onFilterChange(formValue: any) {
    if(this.firstFilterPassed){
      this.lastFilter = formValue;
      this.applyFilters(this.items, formValue);
      this.pageChange.emit({
        pageNo: this.pageNo,
        pageSize: this.pageSize
      });
    } else {
      this.firstFilterPassed = true;
    }
  }

  clearFilter() {
    this.formComponent.form.reset();
    this.filterEvent.emit(this.formComponent.form.value);
    this.clearFilterEvent.emit();
  }
}
